/**
 * Launcher V2 U2a / D14 — the PURE half of the usage-sidebar decision
 * pipeline, split out of `use-usage-sidebar-decision.ts` so that
 * `bun test --isolate`-run files never pull React or `_shared/hooks` into
 * `tsconfig.tests.json`'s program.
 *
 * Why the split exists: `use-usage-sidebar-decision.ts`'s React hook
 * transitively imports `mini-apps/_shared/hooks` (for `useApi`), whose
 * barrel (`_shared/hooks/index.ts`) also re-exports `useDraftState` and
 * `useGongFlowBrowseController` — both of which import real
 * `web/app-home/src/**` modules (`hooks/useDraftState.ts`,
 * `components/flow/EnrollQueue/gong-flow-queries.ts`). Those web files are
 * NOT covered by `tsconfig.tests.json` (no `esnext`/`node16` module target,
 * so their `import.meta` usage hard-errors) — so any bun test file that
 * imports the hook file drags a whole tree of unrelated, uncompilable web
 * files into the test program. See coding-standards.md §5 for the resulting
 * repo rule: any `__tests__` file under `mini-apps` may only import modules
 * whose transitive imports stay inside `mini-apps` and `src/shared`.
 *
 * This module has NO React import and NO `_shared/hooks` import — only
 * `./mutations` (for {@link isSharedMappingConflictError}), `./conflict-errors`,
 * and the usage-sidebar store, all of which stay inside `mini-apps/**`. `bun test`s target
 * THIS module, never the hook file.
 */
import { isVersionConflictError } from './conflict-errors';
import { isSharedMappingConflictError } from './mutations';
import type { PlaybooksUsageResponse } from './schemas';
import {
  markUsageSidebarStale,
  resetUsageSidebarToIdle,
  useUsageSidebarStore,
  type PendingUsageSidebar,
} from './usage-sidebar-store';

/** The plain-text terminal message — reused verbatim, never the server's raw `detail`. */
export const USAGE_SIDEBAR_CONFLICT_RETRY_MESSAGE =
  "This sequence's places changed again while you were saving. Please try once more.";

/**
 * One handler per legal `UsageSidebarDecision` value. `delete` is unused
 * until U2b wires a caller — the slot exists now so that unit only wires it.
 *
 * D15 (Codex F-B) — every handler receives `isStillCurrent` alongside
 * `pending`: a predicate the handler itself must check, right after each
 * network write settles, before performing any client-side continuation
 * (cache mutation, draft clear, navigation, success/error toast). The old
 * shape checked this ONLY in `use-usage-sidebar-decision.ts`'s own
 * `.then`/`.catch`, AFTER `await handler(pending)` had already fully run —
 * so a handler whose write outlives a replacement request (an older
 * `openUsageSidebar` call's write settling after a newer one has taken
 * over) still mutated cache/cleared drafts/toasted for a request that is no
 * longer the one on screen. Passing the predicate into the handler is what
 * lets it stop BEFORE those continuations rather than after all of them.
 */
export interface UsageSidebarDecisionHandlers {
  updateAll?: (pending: PendingUsageSidebar, isStillCurrent: () => boolean) => Promise<void>;
  saveNew?: (pending: PendingUsageSidebar, isStillCurrent: () => boolean) => Promise<void>;
  /**
   * Plan 2026-09-28-006 U3b (rulebook R6, R8) — option ② of the Save menu,
   * "Update N plays" / "Update N groups in this play". Dispatched for
   * `pending.decision === 'update-plays'`; hosts (U4/U5/U6, waves 3-4) branch
   * on `pending.target.owningPlayId` themselves (owned → a direct write with
   * `{ playId }`; Library → `mutations.ts#updatePlays` + `adoptUpdatePlaysResult`)
   * — this dispatcher only routes to whichever host handler is wired.
   */
  updatePlays?: (pending: PendingUsageSidebar, isStillCurrent: () => boolean) => Promise<void>;
  delete?: (pending: PendingUsageSidebar, isStillCurrent: () => boolean) => Promise<void>;
  /**
   * Codex fixes 2026-09-17, F2 — no write; the host reopens the Generate
   * drawer (`resumeReviewFromPending`). `openGenerateSession` closes the
   * sidebar itself, so the hook's own `.then` sees `isStillCurrent() ===
   * false` and does nothing more.
   */
  resumeReview?: (pending: PendingUsageSidebar, isStillCurrent: () => boolean) => Promise<void>;
}

/**
 * The part of a fresh usage read the pending request carries. Plan
 * 2026-09-28-008 U1 (Codex round-3 F4) — the whole of it, not `places`
 * alone: a refreshed list beside a stale `ownVersionPlays` /
 * `templatePlaybooks` mislabels ② and names the wrong playbook in ③.
 */
export type UsageSidebarFreshUsage = Pick<PlaybooksUsageResponse, 'places' | 'ownVersionPlays' | 'templatePlaybooks'>;

export type UsageSidebarDecisionOutcome =
  | { outcome: 'success' }
  | { outcome: 'no-handler' }
  | { outcome: 'refetch-and-reopen'; usage: UsageSidebarFreshUsage }
  | { outcome: 'terminal-conflict' };

/**
 * Runs the handler matching `pending.decision` and reports what happened.
 * Throws (does not swallow) any non-`shared_mapping_conflict` error the
 * handler raises — the caller's own catch is the terminal one that decides
 * how to present a genuine failure; this helper only ever intercepts the
 * one error shape it has a defined retry policy for.
 *
 * `retry` is omitted entirely when the caller only wants dispatch-and-report
 * (no conflict handling) — the conflict branch below is unreachable without
 * it and any conflict is simply re-thrown, matching "no policy configured".
 */
export async function runUsageSidebarDecision(
  pending: PendingUsageSidebar,
  handlers: UsageSidebarDecisionHandlers,
  retry?: {
    /** A fresh (never cached) usage fetch — the caller wires `staleTime: 0`. */
    fetchUsage: () => Promise<UsageSidebarFreshUsage>;
    /** Whether the decision immediately preceding this one at this origin already conflicted once. */
    hasConflictedOnce: boolean;
  },
  /**
   * D15 (Codex F-B) — forwarded verbatim into whichever handler runs.
   * Defaults to `() => true` so a caller with nothing stale to check against
   * (and this module's own dispatch/retry tests, which don't exercise
   * staleness) keeps working unchanged.
   */
  isStillCurrent: () => boolean = () => true,
): Promise<UsageSidebarDecisionOutcome> {
  const handler =
    pending.decision === 'update-all'
      ? handlers.updateAll
      : pending.decision === 'save-new'
        ? handlers.saveNew
        : pending.decision === 'update-plays'
          ? handlers.updatePlays
          : pending.decision === 'delete'
            ? handlers.delete
            : pending.decision === 'resume-review'
              ? handlers.resumeReview
              : undefined;
  if (!handler) return { outcome: 'no-handler' };

  try {
    await handler(pending, isStillCurrent);
    return { outcome: 'success' };
  } catch (error) {
    if (!isSharedMappingConflictError(error) || retry === undefined) throw error;
    if (retry.hasConflictedOnce) return { outcome: 'terminal-conflict' };
    const { places, ownVersionPlays, templatePlaybooks } = await retry.fetchUsage();
    return { outcome: 'refetch-and-reopen', usage: { places, ownVersionPlays, templatePlaybooks } };
  }
}

/**
 * Plan 2026-09-28-008 U1 (rulebook R14; replaces 2026-09-28-007 U5's
 * close-on-conflict) — what the decision hook's `.catch` does with a failed
 * decision. Returns the sentence to toast, or `null` when there is nothing
 * to toast. Only acts while `requestId` is still the pending request (a
 * later `openUsageSidebar` must never be marked or reset by an older one).
 *
 *  - 409 `version_conflict`: someone else changed the group or the piece
 *    first. The SAME pending is parked stale ({@link markUsageSidebarStale})
 *    with the server's sentence — the Save panel shows it with a "Reload to
 *    see their change" button and keeps the rep's draft. Nothing refetches,
 *    adopts or reseeds here: only the rep's Reload does
 *    (`usage-sidebar-reload.ts`). Returns `null` — the panel is where the
 *    sentence lives, a toast would say it twice.
 *  - anything else: back to idle — the same pending, so the rep can pick
 *    again — and the sentence is returned for a toast.
 *
 * The sentence is the server's own (never ids, codes or paths — the server
 * owns that rule), or a plain fallback.
 */
export function settleUsageSidebarFailure(error: unknown, requestId: string): string | null {
  const stillCurrent = useUsageSidebarStore.getState().pending?.requestId === requestId;
  const sentence = error instanceof Error ? error.message : 'Could not complete this action — try again.';
  if (isVersionConflictError(error)) {
    if (stillCurrent) markUsageSidebarStale(requestId, sentence);
    return null;
  }
  if (stillCurrent) resetUsageSidebarToIdle(requestId);
  return sentence;
}
